// ============================================================
//  src/context/AuthContext.js
// ============================================================
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { postLogin, getUser } from '../api/client';

const AuthContext = createContext(null);

const INACTIVITY_MS = 10 * 60 * 1000; // 10 minutes
const EVENTS        = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [token,   setToken]   = useState(() => localStorage.getItem('authToken'));
  const [loading, setLoading] = useState(true);
  const timerRef              = useRef(null);

  // ── Persist token ──────────────────────────────────────
  const saveToken = useCallback((tk) => {
    setToken(tk);
    if (tk) localStorage.setItem('authToken', tk);
    else    localStorage.removeItem('authToken');
  }, []);

  // ── Logout ─────────────────────────────────────────────
  const logout = useCallback(() => {
    saveToken(null);
    setUser(null);
    localStorage.removeItem('authUser');
  }, [saveToken]);

  // ── Inactivity auto-logout ─────────────────────────────
  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      logout();
    }, INACTIVITY_MS);
  }, [logout]);

  useEffect(() => {
    if (!user) return; // only track when logged in
    EVENTS.forEach((ev) => window.addEventListener(ev, resetTimer, { passive: true }));
    resetTimer(); // start timer immediately on login
    return () => {
      EVENTS.forEach((ev) => window.removeEventListener(ev, resetTimer));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [user, resetTimer]);

  // ── Bootstrap: rehydrate user from stored token ────────
  useEffect(() => {
    const bootstrap = async () => {
      const stored = localStorage.getItem('authToken');
      if (!stored) { setLoading(false); return; }
      try {
        const { data: res } = await getUser();
        if (res.status === 'success') {
          setUser(res);
          setToken(stored);
        } else {
          saveToken(null);
          localStorage.removeItem('authUser');
        }
      } catch {
        saveToken(null);
        localStorage.removeItem('authUser');
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, []); // eslint-disable-line

  // ── Login ──────────────────────────────────────────────
  const login = useCallback(async (phone, accesspass) => {
    const { data: res } = await postLogin({ phone, accesspass });
    if (res.status === 'success') {
      const apiKey = res.api_key;
      saveToken(apiKey);
      const { data: profile } = await getUser();
      if (profile.status === 'success') {
        setUser(profile);
        localStorage.setItem('authUser', JSON.stringify(profile));
      }
      return res;
    }
    throw new Error(res.msg || 'Login failed');
  }, [saveToken]);

  // ── Refresh User (partial merge) ───────────────────────
  const refreshUser = useCallback(async () => {
    try {
      const { data: res } = await getUser();
      if (res.status === 'success') setUser(res);
    } catch { /* silent */ }
  }, []);

  // ── Merge partial update into user state ───────────────
  const updateUser = useCallback((partial) => {
    setUser((prev) => ({ ...prev, ...partial }));
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, refreshUser, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);